import { FC, useCallback } from 'react';

import { SearchableSelect } from 'components';
import { TextField, ValidationResult, ValidationStatus } from 'components/inputs/text-field';
import useTimeZone from 'hooks/use-timezone';
import { getBookingByNumber } from 'services';
import { sslScacs } from 'types';

import { useEditBookings } from './edit-booking.context';

const GeneralInformation: FC = () => {
  const { booking, setBooking } = useEditBookings();
  const { dateInputToUtc, utcToZonedDate } = useTimeZone();

  const validateBookingNumber = useCallback(
    async (bookingNumber: string): Promise<ValidationResult> => {
      if (!bookingNumber) {
        return { status: ValidationStatus.DEFAULT };
      } else if (!booking?.customer?.id) {
        return { status: ValidationStatus.DEFAULT, error: 'Account required for validation' };
      } else if (!booking?.location?.id) {
        return { status: ValidationStatus.DEFAULT, error: 'Location required for validation' };
      }
      const response = await getBookingByNumber(bookingNumber, booking.customer.id, booking.location.id);
      if (!response || response.id === booking.id) {
        return { status: ValidationStatus.SUCCESS };
      }
      return { status: ValidationStatus.WARNING, error: 'Booking number already in use by this customer' };
    },
    [booking?.customer, booking?.id, booking?.location]
  );

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>GENERAL INFORMATION</h4>
      </div>
      <div className="card-body">
        <div className="grid-4 p-2">
          <div className="position-relative">
            <h6 className="text-info-dark">BOOKING NUMBER</h6>
            <TextField
              className="mt-2"
              value={booking?.bookingNumber ?? ''}
              onChange={bn => setBooking(b => b && { ...b, bookingNumber: bn.substring(0, 20) })}
              validate={validateBookingNumber}
              required
            />
          </div>
          <div>
            <h6 className="text-info-dark">ACCOUNT</h6>
            <input type="text" className="mt-2 w-100" value={booking?.customer?.name} disabled />
          </div>
          <div className="col-span-2 row-span-4 h-100 d-flex flex-column">
            <h6 className="text-info-dark">NOTES</h6>
            <textarea
              className="mt-2 w-100 flex-fill"
              onChange={e => setBooking(b => b && { ...b, notes: e.target.value })}
              value={booking?.notes ?? ''}
            />
          </div>
          <div>
            <h6 className="text-info-dark">LOCATION</h6>
            <input type="text" className="mt-2 w-100" value={booking?.location?.name} disabled />
          </div>
          <div className="position-relative">
            <h6 className="text-info-dark">RAILYARD</h6>
            <input
              type="text"
              className="mt-2 w-100 text-uppercase"
              value={booking?.railyard ?? ''}
              onChange={e => setBooking(b => b && { ...b, railyard: e.target.value })}
            />
          </div>
          <div>
            <h6 className="text-info-dark">START DATE</h6>
            <input
              type="date"
              className="mt-2 w-100"
              onChange={e => setBooking(b => b && { ...b, startDate: dateInputToUtc(e.target.value) })}
              value={utcToZonedDate(booking?.startDate ?? booking?.createdDate)}
              max={utcToZonedDate(booking?.expirationDate)}
              required
            />
          </div>
          <div>
            <h6 className="text-info-dark">EXPIRATION DATE</h6>
            <input
              type="date"
              className="mt-2 w-100"
              onChange={e => setBooking(b => b && { ...b, expirationDate: dateInputToUtc(e.target.value) })}
              value={utcToZonedDate(booking?.expirationDate)}
              min={utcToZonedDate(booking?.startDate)}
              required
            />
          </div>
          <div>
            <h6 className="text-info-dark">SSL SCAC</h6>
            <SearchableSelect
              options={sslScacs.map(scac => ({ label: scac, value: scac }))}
              value={booking?.sslScac ?? ''}
              onValueSelected={scac => setBooking(b => b && { ...b, sslScac: scac })}
              data-cy='searchable-select-ssl-scac'
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralInformation;
